'use client';

import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import tippy, { type Instance as TippyInstance } from 'tippy.js';
import {
  MentionList,
  type MentionListRef,
} from '@/src/sections/social/components/MentionList';

export type MentionItem = {
  id: number;
  uniqueLink: string;
  fullName?: string;
  avatarUrl?: string;
};

export function makeMentionExtension(opts: {
  postId?: number | null;
  fetchUrl: (q: string) => string;
  onPicked?: (item: MentionItem) => void;
}) {
  return Mention.configure({
    HTMLAttributes: { class: 'mention' },

    suggestion: {
      char: '@',
      items: () => [],

      render: () => {
        let component: ReactRenderer<MentionListRef> | null = null;
        let popup: TippyInstance[] | null = null;

        let reqId = 0;
        let lastQuery = '';
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;

        let latestTiptapCommand: ((attrs: any) => void) | null = null;

        const setLatestCommand = (props: any) => {
          latestTiptapCommand = props.command;
          component?.updateProps({
            command: (item: MentionItem) => {
              opts.onPicked?.(item);
              const unique = String(item.uniqueLink ?? '').replace(/^@/, '');
              latestTiptapCommand?.({
                id: unique,
                label: unique,
              });
            },
          });
        };

        const load = async (query: string, clientRect: any) => {
          const myReq = ++reqId;
          component?.updateProps({ isLoading: true });

          try {
            const res = await fetch(opts.fetchUrl(query));
            const json = await res.json();
            const items = (json?.items ??
              json?.data?.items ??
              []) as MentionItem[];

            if (myReq !== reqId) return;

            component?.updateProps({ items, isLoading: false });

            popup?.[0]?.setProps({ getReferenceClientRect: clientRect });
          } catch {
            if (myReq !== reqId) return;
            component?.updateProps({ items: [], isLoading: false });
          }
        };

        const debouncedLoad = (query: string, clientRect: any) => {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => void load(query, clientRect), 250);
        };

        return {
          onStart: (props) => {
            lastQuery = props.query ?? '';

            component = new ReactRenderer(MentionList, {
              props: {
                items: [],
                isLoading: true,
                command: () => {},
              },
              editor: props.editor,
            });

            popup = tippy('body', {
              getReferenceClientRect: props.clientRect as any,
              appendTo: () => document.body,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
            });

            setLatestCommand(props);

            void load(lastQuery, props.clientRect as any);
          },

          onUpdate: (props) => {
            const q = props.query ?? '';

            popup?.[0]?.setProps({
              getReferenceClientRect: props.clientRect as any,
            });

            setLatestCommand(props);

            if (q === lastQuery) return;
            lastQuery = q;

            debouncedLoad(q, props.clientRect as any);
          },

          onKeyDown: (props) => {
            if (props.event.key === 'Escape') {
              popup?.[0]?.hide();
              return true;
            }
            return (component?.ref as any)?.onKeyDown?.(props) ?? false;
          },

          onExit: () => {
            if (debounceTimer) clearTimeout(debounceTimer);

            popup?.[0]?.destroy();
            popup = null;

            component?.destroy();
            component = null;

            latestTiptapCommand = null;
            reqId++;
          },
        };
      },
    },

    renderHTML: ({ node }) => {
      const uniqueLink = String(node.attrs.id ?? '').replace(/^@/, '');
      const label = String(node.attrs.label ?? uniqueLink);

      return [
        'span',
        {
          class: 'mention',
          'data-type': 'mention',
          'data-id': uniqueLink,
          'data-label': label,
          'data-mention-suggestion-char': '@',
        },
        `@${label}`,
      ];
    },
  });
}
